import React, { useState, useEffect, useCallback, useRef } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ACTION_COLORS = {
  LOGIN:             { bg: "#22c55e22", text: "#22c55e", border: "#22c55e44" },
  LOGOUT:            { bg: "#64748b22", text: "#94a3b8", border: "#64748b44" },
  FAILED_LOGIN:      { bg: "#ef444422", text: "#ef4444", border: "#ef444444" },
  ACCOUNT_LOCKED:    { bg: "#dc262622", text: "#dc2626", border: "#dc262644" },
  USER_CREATED:      { bg: "#3b82f622", text: "#3b82f6", border: "#3b82f644" },
  USER_DEACTIVATED:  { bg: "#f59e0b22", text: "#f59e0b", border: "#f59e0b44" },
  ROLE_CHANGED:      { bg: "#8b5cf622", text: "#a78bfa", border: "#8b5cf644" },
  PERMISSION_CHANGED:{ bg: "#06b6d422", text: "#22d3ee", border: "#06b6d444" },
  PASSWORD_CHANGED:  { bg: "#f97316222", text: "#fb923c", border: "#f9731644" },
};

const ACTION_OPTIONS = [
  "LOGIN", "LOGOUT", "FAILED_LOGIN", "ACCOUNT_LOCKED",
  "USER_CREATED", "USER_DEACTIVATED", "ROLE_CHANGED",
  "PERMISSION_CHANGED", "PASSWORD_CHANGED",
];

function ActionBadge({ action }) {
  const c = ACTION_COLORS[action] || { bg: "#33333322", text: "#94a3b8", border: "#33333344" };
  return (
    <span style={{
      background: c.bg, color: c.text, border: `1px solid ${c.border}`,
      borderRadius: 20, padding: "2px 10px", fontSize: 10, fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {action.replace(/_/g, " ")}
    </span>
  );
}

function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("en-ZW", { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

export function AuditTrailPage({ user, theme: t, isMobile }) {
  const [entries,    setEntries]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState(null);

  // Filters
  const [search,   setSearch]   = useState("");
  const [actionF,  setActionF]  = useState("");
  const [userF,    setUserF]    = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");

  const refreshRef = useRef(null);

  const fetchData = useCallback(async (pg = page) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: pg, limit: 50 });
    if (search)   params.set("search",   search);
    if (actionF)  params.set("action",   actionF);
    if (userF)    params.set("userId",   userF);
    if (dateFrom) params.set("dateFrom", dateFrom);
    if (dateTo)   params.set("dateTo",   dateTo);
    try {
      const res = await fetchWithAuth(`${API}/api/admin/audit-trail?${params}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setEntries(data.entries || []);
      setTotal(data.total || 0);
      setPage(data.page || pg);
      setTotalPages(data.totalPages || 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, search, actionF, userF, dateFrom, dateTo]);

  // Initial load + 30s auto-refresh
  useEffect(() => {
    fetchData(1);
    clearInterval(refreshRef.current);
    refreshRef.current = setInterval(() => fetchData(1), 30000);
    return () => clearInterval(refreshRef.current);
  }, [search, actionF, userF, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePageChange(p) {
    setPage(p);
    fetchData(p);
  }

  function exportCSV() {
    const header = ["Timestamp", "User", "Role", "Action", "Details", "IP", "Session"];
    const rows = entries.map(e => [
      e.timestamp, e.userName, e.userRole, e.action,
      `"${(e.details || "").replace(/"/g, "'")}"`,
      e.ip, e.sessionId || "",
    ]);
    const csv = [header, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `audit-trail-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const inputStyle = {
    background: t.surface2, border: `1px solid ${t.border}`, color: t.text,
    borderRadius: 8, padding: "7px 12px", fontSize: 12,
    outline: "none", fontFamily: "inherit",
  };

  return (
    <div>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Audit Trail</div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
            {total.toLocaleString()} entries · auto-refreshes every 30s
          </div>
        </div>
        <button onClick={exportCSV}
          style={{ background: "#2563EB", border: "none", borderRadius: 8, padding: "8px 16px", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr 1fr 1fr", gap: 10, marginBottom: 18 }}>
        <input
          style={{ ...inputStyle, gridColumn: isMobile ? "1/-1" : undefined }}
          placeholder="Search user, action, details…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select style={inputStyle} value={actionF} onChange={e => setActionF(e.target.value)}>
          <option value="">All Actions</option>
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
        </select>
        <input
          style={inputStyle}
          placeholder="Filter by user…"
          value={userF}
          onChange={e => setUserF(e.target.value)}
        />
        <input type="date" style={inputStyle} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
        <input type="date" style={inputStyle} value={dateTo}   onChange={e => setDateTo(e.target.value)}   title="To date" />
      </div>

      {/* Error */}
      {error && (
        <div style={{ background: "#dc262611", border: "1px solid #dc262633", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#dc2626", marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 14, overflow: "hidden" }}>
        {/* Table header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "140px 1fr 90px" : "175px 160px 80px 145px 1fr 110px",
          gap: 0,
          background: t.surface2,
          borderBottom: `1px solid ${t.border}`,
          padding: "9px 16px",
        }}>
          {(isMobile
            ? ["Timestamp", "Action / User", "IP"]
            : ["Timestamp", "User", "Role", "Action", "Details", "IP"]
          ).map(h => (
            <div key={h} style={{ fontSize: 9, fontWeight: 800, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        {loading && entries.length === 0 ? (
          <div style={{ padding: "30px 0", textAlign: "center", fontSize: 12, color: t.textFaint }}>Loading…</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: "30px 0", textAlign: "center", fontSize: 12, color: t.textFaint }}>No entries found</div>
        ) : entries.map((e, i) => (
          <div key={e.id} style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "140px 1fr 90px" : "175px 160px 80px 145px 1fr 110px",
            gap: 0,
            padding: "10px 16px",
            borderBottom: i < entries.length - 1 ? `1px solid ${t.border}` : "none",
            background: i % 2 === 1 ? t.surface2 + "55" : "transparent",
            alignItems: "center",
          }}>
            {isMobile ? (
              <>
                <div style={{ fontSize: 10, color: t.textSub }}>{fmt(e.timestamp)}</div>
                <div>
                  <ActionBadge action={e.action} />
                  <div style={{ fontSize: 10, color: t.textMuted, marginTop: 3 }}>{e.userName}</div>
                </div>
                <div style={{ fontSize: 10, color: t.textFaint, fontFamily: "monospace" }}>{e.ip}</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 10, color: t.textSub, fontFamily: "monospace" }}>{fmt(e.timestamp)}</div>
                <div>
                  <div style={{ fontSize: 12, color: t.text, fontWeight: 600 }}>{e.userName}</div>
                </div>
                <div style={{ fontSize: 10, color: t.textMuted, textTransform: "capitalize" }}>{e.userRole?.replace("_", " ")}</div>
                <div><ActionBadge action={e.action} /></div>
                <div style={{ fontSize: 11, color: t.textSub }}>{e.details || "—"}</div>
                <div style={{ fontSize: 10, color: t.textFaint, fontFamily: "monospace" }}>{e.ip}</div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 18 }}>
          <button
            onClick={() => handlePageChange(page - 1)} disabled={page <= 1}
            style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 6, padding: "5px 12px", color: page <= 1 ? t.textFaint : t.text, fontSize: 12, cursor: page <= 1 ? "default" : "pointer" }}>
            ← Prev
          </button>
          <span style={{ fontSize: 12, color: t.textMuted }}>Page {page} / {totalPages}</span>
          <button
            onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}
            style={{ background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 6, padding: "5px 12px", color: page >= totalPages ? t.textFaint : t.text, fontSize: 12, cursor: page >= totalPages ? "default" : "pointer" }}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
